<template>
  <div class="organization-units">
    <!-- Company -->
    <section>
      <h5>{{ client.allocation.label }} Portion</h5>

      <div class="grid grid-cols-10 flex-col-sm">
        <span class="col-span-3 self-start ml-6 order-1">Allocation</span>
        <span class="col-span-2 self-end order-2 text-end-sm"></span>
        <span class="col-span-2 self-end order-3 text-end-sm"></span>
        <span class="col-span-3 self-end order-4 text-end-sm"></span>

        <span class="col-span-3 self-start ml-6 order-1">{{ formatPercent(client.allocation.weight) }}</span>
        <span class="col-span-2 self-center order-2 text-end-sm">{{ formatCurrency(client.allocation.funding) }}</span>
        <span class="col-span-2 self-end order-3 text-end-sm"></span>
        <span class="col-span-3 self-end order-4 text-end-sm">{{ formatCurrency(client.allocation.contribution) }}</span>
      </div>
    </section>

    <!-- Organization Units -->
    <section
      v-for="(unit, index) in Object.values(organization.units)"
      :key="`org-units-${index}`"
    >
      <h5>{{ organization.types[unit.type_id].label }} Portion</h5>

      <div class="grid grid-cols-10 flex-col-sm">
        <span class="col-span-3 self-start ml-6 order-1">Allocation</span>
        <span class="col-span-2 self-end order-2 text-end-sm"></span>
        <span class="col-span-3 self-center order-3 text-end-sm">Perf Mod %</span>
        <span class="self-center order-4 text-end-sm text-highlight mr-13-sm">+</span>
        <span class="self-end order-4 text-end-sm"></span>

        <span class="col-span-3 self-start ml-6 order-1">{{ formatPercent(unit.allocation.weight) }}</span>
        <span class="col-span-2 self-center order-2 text-end-sm">{{ formatCurrency(unit.allocation.funding) }}</span>
        <span class="self-center order-2 text-end-sm">x</span>
        <span class="self-end order-3 text-end-sm">
          <template v-if="unit.allocation.modifier">{{ formatPercent(unit.allocation.modifier) }}</template>
          <template v-else>N/A</template>
        </span>
        <span class="col-span-2 self-center order-4 text-end-sm">=</span>
        <span class="self-end order-4 text-end-sm">{{ formatCurrency(unit.allocation.contribution) }}</span>
      </div>
    </section>

    <!-- Individual -->
    <section v-if="employee.performance.mode == 'as-component'">
      <h5>{{ employee.allocation.label }} Portion</h5>

      <div class="grid grid-cols-10 flex-col-sm">
        <span class="col-span-3 self-start ml-6 order-1">Allocation</span>
        <span class="col-span-2 self-end order-2 text-end-sm"></span>
        <span class="col-span-3 self-end order-3 text-end-sm">Perf Mod %</span>
        <span class="self-end order-4 text-end-sm text-highlight mr-13-sm">+</span>
        <span class="self-end order-4 text-end-sm"></span>

        <span class="col-span-3 self-start ml-6 order-1">{{ formatPercent(employee.allocation.weight) }}</span>
        <span class="col-span-2 self-center order-2 text-end-sm">{{ formatCurrency(employee.allocation.funding) }}</span>
        <span class="self-center order-3 text-end-sm">x</span>
        <span class="self-end order-3 text-end-sm">
          <template v-if="employee.allocation.modifier">{{
            formatPercent(employee.allocation.modifier)
          }}</template>
          <template v-else>N/A</template>
        </span>
        <span class="col-span-2 self-center order-4 text-end-sm">=</span>
        <span class="self-end order-4 text-end-sm">{{ formatCurrency(employee.allocation.contribution) }}</span>
      </div>
    </section>

    <section>
      <h5></h5>
      <div class="summary grid grid-cols-10 flex-col-sm">
        <span class="col-span-3 self-start ml-6 order-1"></span>
        <span class="col-span-2 self-end order-2 text-end-sm"></span>
        <span class="col-span-2 self-end order-3 text-end-sm"></span>
        <span class="col-span-3 self-end order-4 text-end-sm"></span>
        <span class="col-span-3 self-start ml-6 order-1">{{ formatPercent(organization.totals.weight) }}</span>
        <span class="col-span-2 self-center order-2 text-end-sm">{{ formatCurrency(organization.totals.funding) }}</span>
        <span class="self-center order-3 text-end-sm">x</span>
        <span class="self-end order-3 text-end-sm">{{ formatPercent(organization.totals.modifier) }}</span>
        <span class="col-span-2 self-center order-4 text-end-sm">=</span>
        <span class="self-end order-4 text-end-sm">{{ formatCurrency(organization.totals.contribution) }}</span>
      </div>
    </section>
  </div>
</template>

<script>
import VLcurrency from "@/composables/VLcurrency";
import VLpercent from '@/composables/VLpercent';

export default {
    name: "OrganizationUnits",

    props: {
        client: {
            type: Object,
            required: true,
        },
        organization: {
            type: Object,
            required: false,
        },
        employee: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {}
    },

    computed: {},

    methods: {
        formatCurrency(value, decimals = 0, currency = 'USD') {
            return VLcurrency.formatCurrency(value, decimals, currency);
        },
        formatPercent(value, decimals = 0) {
            return VLpercent.formatPercent(value, decimals);
        }
    },
}
</script>

<style lang="scss" scoped>
@use "../../../../scss/_variables.scss";

.text-highlight {
    color: var(--highlight-color);
}
.organization-units {
    margin-bottom: 1.125rem;

    h5 {
        font-size: variables.$text-sm;
    }

    section {
        padding-bottom: 0.2rem;
        margin-bottom: 1.125rem;
        border-bottom: 2px solid variables.$cloud;

        span:nth-child(-n+4) {
            font-size: variables.$text-sm;
        }

        span:nth-child(n+5) {
            font-size: variables.$text-base;
            color: variables.$charcoal-tint-50;
        }

        span:last-child {
            font-family: variables.$font-secondary;
            font-weight: 800;
        }
    }

    .summary {
        font-size: variables.$text-base;

        span:last-child {
            font-weight: bold;
            font-family: variables.$font-secondary;
            font-weight: 800;
        }
    }
}

.grid {
    display: grid;

    &-cols {
        &-4 {
            grid-template-columns: repeat(4, 1fr);
        }

        &-8 {
            grid-template-columns: repeat(8, 1fr);
        }

        &-10 {
            grid-template-columns: repeat(10, 1fr);
        }

        &-12 {
            grid-template-columns: repeat(12, 1fr);
        }
    }
}

.col-span {
    grid-column: span 1;

    &-1 {
        grid-column: span 1;
    }

    &-2 {
        grid-column: span 2;
    }

    &-3 {
        grid-column: span 3;
    }

    &-4 {
        grid-column: span 4;
    }
}

.self {

    &-start {
        justify-self: start;
    }

    &-end {
        justify-self: end;
    }

    &-center {
        justify-self: center;
    }
}

.ml {
    &-2 {
        margin-left: 0.5rem;
    }

    &-4 {
        margin-left: 1rem;
    }

    &-6 {
        margin-left: 1.5rem;
    }
}

@media screen and (max-width: 650px) {
    .flex-col-sm {
        display: flex !important;
        flex-direction: column;
    }

    .text-end-sm {
      text-align: end;
    }

    .mr-13-sm {
        margin-right: 5rem;
    }

    .ml-6 {
        margin-left: 0;
    }

    .order {
        &-1 {
            order: 1;
        }
        &-2 {
            order: 2;
        }
        &-3 {
            order: 3;
        }
        &-4 {
            order: 4;
        }
    }
}
</style>